import { SecLoanCarOwnershipType, SecLoanChildrenAgeGroup } from '@shared/sec-loans'
import { YesNo } from '@ui-common/types'
import { buildOptionList } from '@ui-common/utils'
import { AddItemButton } from '@ui-components/actions/special/AddItemButton'
import { Divider } from '@ui-components/decorative/Divider'
import { FormRadioGroup } from '@ui-components/form/FormRadioGroup'
import { FormRemovableSectionHeader } from '@ui-components/form/FormRemoveableSectionHeader'
import { FormTextInput } from '@ui-components/form/FormTextInput'
import { Fragment } from 'react'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useToggleableDynamicFormField } from '../../../hooks/useToggleableDynamicFormField'
import { getIfpApplicantInfoLocalizedEnums } from './applicantInfoStepUtils'
import { ApplicantInfoFormData } from './types'

export const ExpensesFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({
    name: [
      'ownShareOfHousingExpenses',
      'hasCarOwnership',
      'carOwnershipType',
      'totalLoansAmount',
      'totalAmountOfLoanRepaymentsPerMonth',
      'payingHousingCompanyLoan',
      'totalHousingCompanyLoan',
    ],
    control,
  })
  const [selectedHasCarOwnership, payingHousingCompanyLoan] = useWatch({
    name: ['hasCarOwnership', 'payingHousingCompanyLoan'],
    control,
  })
  const { yesNoTexts, expenseInfoCarOwnershipLabelTexts } = getIfpApplicantInfoLocalizedEnums(formatMessage)

  return (
    <>
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'ifp-loans-applicant-info-own-share-of-housing-expenses-label' })}
        description={formatMessage({ id: 'ifp-loans-applicant-info-own-share-of-housing-expenses-description' })}
        error={errors?.ownShareOfHousingExpenses?.message}
        {...register('ownShareOfHousingExpenses')}
      />
      <FormRadioGroup
        label={formatMessage({ id: 'sec-loans-expenses-info-has-car-ownership-form-label' })}
        required={true}
        options={buildOptionList(Object.values(YesNo), yesNoTexts)}
        error={errors?.hasCarOwnership?.message}
        {...register('hasCarOwnership')}
      >
        {selectedHasCarOwnership === YesNo.Yes && (
          <FormRadioGroup
            label={formatMessage({ id: 'sec-loans-expenses-info-car-ownership-form-label' })}
            required={true}
            description={formatMessage({
              id: 'ifp-loans-applicant-info-car-ownership-type-description',
            })}
            options={buildOptionList(Object.values(SecLoanCarOwnershipType), expenseInfoCarOwnershipLabelTexts)}
            {...register('carOwnershipType')}
            error={errors.carOwnershipType?.message}
          />
        )}
      </FormRadioGroup>
      <ChildSupportFormFields />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'ifp-loans-total-loans-amount-label' })}
        description={formatMessage({ id: 'ifp-loans-applicant-info-only-own-share-description' })}
        error={errors?.totalLoansAmount?.message}
        {...register('totalLoansAmount')}
      />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'ifp-loans-applicant-info-total-amount-of-loan-repayments-per-month' })}
        description={formatMessage({ id: 'ifp-loans-applicant-info-only-own-share-description' })}
        error={errors?.totalAmountOfLoanRepaymentsPerMonth?.message}
        {...register('totalAmountOfLoanRepaymentsPerMonth')}
      />
      <FormRadioGroup
        label={formatMessage({ id: 'ifp-loans-applicant-info-paying-company-loan-label' })}
        required={true}
        options={buildOptionList(Object.values(YesNo), yesNoTexts)}
        error={errors.payingHousingCompanyLoan?.message}
        {...register('payingHousingCompanyLoan')}
      >
        {payingHousingCompanyLoan === YesNo.Yes && (
          <FormTextInput
            required={true}
            inputMode="decimal"
            label={formatMessage({ id: 'ifp-loans-applicant-info-total-housing-company-loan' })}
            description={formatMessage({ id: 'ifp-loans-applicant-info-only-own-share-description' })}
            error={errors?.totalHousingCompanyLoan?.message}
            {...register('totalHousingCompanyLoan')}
          />
        )}
      </FormRadioGroup>
    </>
  )
}

const ChildSupportFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control, watch } = useFormContext<ApplicantInfoFormData>()
  const { errors, defaultValues } = useFormState({
    name: ['isPayingChildSupport', 'childSupports'],
    control,
  })
  const [isPayingChildSupport] = useWatch({
    name: ['isPayingChildSupport'],
    control,
  })
  const { yesNoTexts, expenseInfoChildAgeLabelText } = getIfpApplicantInfoLocalizedEnums(formatMessage)

  const {
    renderFields,
    append: appendChildSupport,
    remove: removeChildSupport,
  } = useToggleableDynamicFormField(
    {
      dataProp: 'childSupports',
      hasProp: 'isPayingChildSupport',
    },
    control,
    watch,
    register,
    defaultValues?.childSupports?.length ?? 0,
    undefined,
    true,
  )

  return (
    <FormRadioGroup
      label={formatMessage({ id: 'sec-loans-expenses-info-has-alimony-form-label' })}
      required={true}
      options={buildOptionList(Object.values(YesNo), yesNoTexts)}
      error={errors.isPayingChildSupport?.message}
      data-testid="is-paying-child-support-form-field"
      {...register('isPayingChildSupport')}
    >
      {isPayingChildSupport === YesNo.Yes && (
        <>
          {renderFields.map((child, index) => (
            <Fragment key={child.id}>
              <FormRemovableSectionHeader
                index={index}
                onClick={(event) => removeChildSupport(event, index)}
                heading={formatMessage({
                  id: 'sec-loans-additional-child-info-label',
                })}
                testID={'ExpensesInfoStepChildAgeForm'}
                dynamicArrayLength={renderFields.length}
                removeActionLabel={formatMessage({ id: 'common-remove' })}
              />
              <FormRadioGroup
                label={formatMessage({ id: 'sec-loans-expenses-alimony-child-age-form-label' })}
                required={true}
                options={buildOptionList(Object.values(SecLoanChildrenAgeGroup), expenseInfoChildAgeLabelText)}
                error={errors.childSupports?.[index]?.ageGroup?.message}
                {...register(`childSupports.${index}.ageGroup`)}
              />
              <FormTextInput
                required={true}
                inputMode="decimal"
                label={formatMessage({ id: 'sec-loans-expenses-alimony-monthly-cost-label' })}
                error={errors.childSupports?.[index]?.supportAmount?.message}
                {...register(`childSupports.${index}.supportAmount`)}
              />
              <Divider />
            </Fragment>
          ))}
          <AddItemButton
            description={formatMessage({
              id: 'sec-loans-expenses-info-alimony-form-add-child-description',
            })}
            label={formatMessage({ id: 'sec-loans-add-new-child-label' })}
            onClick={appendChildSupport}
          />
        </>
      )}
    </FormRadioGroup>
  )
}
